import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

/**
 * PageTransition — classy editorial transition with a subtle distortion ripple.
 *
 *   - The PAGE BENEATH gets an SVG displacement filter that briefly ripples
 *     (peaks mid-transition, clears by end).
 *   - On top: a soft dim veil + a single coral hairline sweep with glow trail.
 *
 * Total duration ~580ms.
 */
export default function PageTransition({ children }) {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [activeKey, setActiveKey] = useState(location.pathname);

  useEffect(() => {
    if (location.pathname === activeKey) return;
    setShow(true);
    const t1 = setTimeout(() => setActiveKey(location.pathname), 230); // mid-distortion swap
    const t2 = setTimeout(() => setShow(false), 580);                  // dissolve out
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.pathname, activeKey]);

  return (
    <>
      {/* Children get the displacement filter briefly during the transition */}
      <div style={show ? { filter: 'url(#pt-distort)' } : undefined}>
        {children}
      </div>

      {/* Inline SVG filter — animated displacement that ripples then clears */}
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }} aria-hidden>
        <defs>
          <filter id="pt-distort" x="-2%" y="-2%" width="104%" height="104%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.012 0.022"
              numOctaves="2"
              seed="3"
              result="noise"
            >
              {show && (
                <>
                  <animate
                    attributeName="baseFrequency"
                    dur="0.55s"
                    values="0.006 0.012; 0.04 0.06; 0.008 0.018"
                    repeatCount="1"
                  />
                  <animate
                    attributeName="seed"
                    dur="0.55s"
                    values="1; 18; 35"
                    repeatCount="1"
                  />
                </>
              )}
            </feTurbulence>
            <feDisplacementMap in="SourceGraphic" in2="noise" xChannelSelector="R" yChannelSelector="G">
              {show && (
                <animate
                  attributeName="scale"
                  dur="0.55s"
                  values="0; 22; 0"
                  keyTimes="0; 0.45; 1"
                  repeatCount="1"
                />
              )}
            </feDisplacementMap>
          </filter>
        </defs>
      </svg>

      <AnimatePresence>
        {show && (
          <motion.div
            key="elegant"
            aria-hidden
            className="fixed inset-0 z-[10000] pointer-events-none"
          >
            {/* Soft dim veil — barely-there */}
            <motion.div
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.55, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.58, times: [0, 0.45, 1], ease: [0.4, 0, 0.2, 1] }}
              style={{
                background:
                  'radial-gradient(ellipse at center, rgba(10,10,15,0.45) 0%, rgba(10,10,15,0.92) 100%)',
              }}
            />

            {/* Thin coral hairline sweeping top→bottom */}
            <motion.div
              className="absolute left-0 right-0 h-px"
              initial={{ top: '0%', opacity: 0 }}
              animate={{ top: ['0%', '50%', '100%'], opacity: [0, 1, 0] }}
              transition={{ duration: 0.58, ease: [0.16, 1, 0.3, 1], times: [0, 0.5, 1] }}
              style={{
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(255,107,74,0.9) 50%, transparent 100%)',
                boxShadow: '0 0 28px 2px rgba(255, 107, 74, 0.6)',
              }}
            />

            {/* Soft glow trail above the hairline */}
            <motion.div
              className="absolute left-0 right-0"
              initial={{ top: '0%', opacity: 0 }}
              animate={{ top: ['0%', '50%', '100%'], opacity: [0, 0.5, 0] }}
              transition={{ duration: 0.58, ease: [0.16, 1, 0.3, 1], times: [0, 0.5, 1] }}
              style={{
                height: 64,
                marginTop: -64,
                background:
                  'linear-gradient(180deg, transparent 0%, rgba(255,107,74,0.10) 80%, rgba(255,107,74,0.22) 100%)',
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
